<script setup>
import emitter from "@/utils/bus"; // 通信对象
import { ref, onUnmounted } from "vue";
import { useConfigStore } from "@/store/config";
import { useI18n } from "vue-i18n";
import Loading from "@/components/Loading.vue";
import { checkStarsOrderStatus } from "@/api/task";
import { checkOrderStatusV2 } from "../api/TON";
import { checkPaymentStatus, appHapticFeedback, addCommasToNumber } from "../utils/common";

const { t } = useI18n();

const config = useConfigStore();
const isShow = ref(false);
const isChecking = ref(false); // 新增：检查中状态
const failCheck = ref(false); // 显示检查失败提示
const taskDetail = ref({
  name: "",
  image: "",
  reward_type: "",
  reward: 0,
});
const orderDetail = ref({
  type: "",
  order_id: "",
});

const close = () => {
  isShow.value = false;
  emitter.emit("orderTaskResult", false); // 触发任务未完成
  appHapticFeedback();
};

// 添加检查结果处理函数
const handleCheck = async () => {
  appHapticFeedback();
  isChecking.value = true; // 开始检查，显示加载状态
  failCheck.value = false;
  let order_status = "";
  if (orderDetail.value.type == "ton") {
    //检查ton
    const check_result = await checkPaymentStatus({
      back: checkOrderStatusV2,
      back_data: orderDetail.value.order_id,
      maxAwaitRetries: 2, //等待的重试次数
      maxErrorRetries: 3, //失败的重试次数
      interval: 10000, //每次检查间隔
    });
    if (check_result.success) {
      order_status = "success";
    }
  } else if (orderDetail.value.type == "stars") {
    //检查星星
    let res = await checkStarsOrderStatus(orderDetail.value.order_id);
    if (res.code == 200 && res.data == "success") {
      order_status = "success";
    }
  }
  // 任务完成
  if (order_status == "success") {
    emitter.emit("orderTaskResult", true);
    isShow.value = false;
  } else {
    failCheck.value = true;
  }
  // 关闭加载中
  isChecking.value = false;
};

const handleShowLoadingModal = async (res) => {
  failCheck.value = false;
  isShow.value = res.isShow;
  taskDetail.value = res.taskDetail;
  orderDetail.value = res.orderDetail;
};

emitter.on("showLoadingModal", handleShowLoadingModal);

onUnmounted(() => {
  emitter.off("showLoadingModal", handleShowLoadingModal);
});
</script>

<template>
  <Transition name="modal">
    <div class="view" v-show="isShow">
      <Transition name="modal-size">
        <div class="language" v-show="isShow">
          <img src="../assets/imgs/wrong.svg" alt="" class="close" @click="close" />
          <div class="content">
            <img :src="taskDetail.image" alt="" class="task-image" />
            <div class="desc">{{ taskDetail.name }}</div>
            <div class="reward">
              +{{ addCommasToNumber(taskDetail.reward) }}
              <img
                src="@/assets/icon/key.svg"
                v-if="taskDetail.reward_type == 'diamond'"
              />
              <img :src="config.coin_image" v-else />
            </div>

            <!-- 检查结果显示区域 -->
            <div class="result-area">
              <!-- 检查中状态 -->
              <div v-if="isChecking" class="checking">
                <Loading class="loading-icon" size="small" color="#000" />
                <span> {{ $t("mentConfirming") }}</span>
              </div>
              <!-- 检查按钮 -->
              <div
                class="check-btn"
                v-if="!isChecking"
                @click="handleCheck"
                :disabled="isChecking"
              >
                {{ $t("message.Check") }}
              </div>
            </div>
            <div class="fail-msg" v-if="failCheck">
              {{ $t("message.CheckFail") }}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
@use "@/styles/surfaces" as surface;

/* 保留原来的弹窗进出效果。 */
.modal-enter-active,
.modal-leave-active,
.modal-size-enter-active,
.modal-size-leave-active {
  transition: all 0.3s ease;
}

.modal-leave-to,
.modal-enter-from {
  opacity: 0;
}

.modal-enter-to,
.modal-leave-from {
  opacity: 1;
}

.modal-size-leave-to,
.modal-size-enter-from {
  transform: scale(0.9);
}

.modal-size-enter-to,
.modal-size-leave-from {
  transform: scale(1);
}

.view {
  @include surface.dialog-overlay;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  backdrop-filter: blur(5px);

  .language {
    @include surface.dialog-panel;
    background: #fff;
    border: #555 solid 1px;
    position: relative;
  }

  .close {
    @include surface.close-button;
    position: absolute;
    top: 8px;
    right: 8px;
  }

  .content {
    // 只为右上角关闭入口留一行空间，内容之间使用固定的小间距。
    margin: 32px 0 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #000;
    white-space: pre-wrap;
    text-align: center;
    font-size: 14px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  .task-image {
    height: 56px;
    max-width: 100%;
    object-fit: contain;
  }

  .desc {
    font-size: 14px;
  }

  .reward {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 20px;
    line-height: 1.4;
    font-weight: bold;

    img {
      width: 20px;
      height: 20px;
    }
  }

  .result-area {
    width: 100%;
    min-height: surface.$control-height;
    margin-top: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .checking {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #999;

    .loading-icon {
      width: 20px;
      height: 20px;
    }
  }

  .check-btn {
    @include surface.button;
    width: 100%;
    border: none;
    background: #000;
    color: #fff;
    font-weight: bold;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .fail-msg {
    font-size: 13px;
    color: #f44336;
  }
}
</style>
